import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { windowLabel } from "@/lib/api";
import { Receipt } from "lucide-react";

export default function BillingPage() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders/my").then((r) => setOrders(r.data)); }, []);

  const paid = orders.filter((o) => o.payment_status === "paid");
  const dueCard = orders.filter((o) => o.payment_status === "pending");
  const inPerson = orders.filter((o) => o.payment_status === "pay_in_person");

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-testid="billing-title">
        <Receipt strokeWidth={2.5}/> Billing & Receipts
      </h1>
      <p className="text-slate-600 mb-4">All your orders, payments, and receipts.</p>

      {dueCard.length > 0 && (
        <Section title="🔴 Payment due">
          {dueCard.map((o) => (
            <div key={o.id} className="card-clay p-5 border-amber-200" data-testid={`due-${o.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-fredoka font-bold">{windowLabel(o.window)} · {o.pickup_date}</div>
                  <div className="text-sm text-slate-600">{o.bags} bag(s) · ${o.total_amount.toFixed(2)}</div>
                </div>
                <Link to={`/checkout/${o.id}`} className="btn-push-amber px-4 py-2 text-sm">Pay now</Link>
              </div>
            </div>
          ))}
        </Section>
      )}

      {inPerson.length > 0 && (
        <Section title="🤝 Pay in person">
          {inPerson.map((o) => (
            <div key={o.id} className="card-clay p-5" data-testid={`inperson-${o.id}`}>
              <div className="font-fredoka font-bold">{windowLabel(o.window)} · {o.pickup_date}</div>
              <div className="text-sm text-slate-600">{o.bags} bag(s) · ${o.total_amount.toFixed(2)}</div>
              <div className="text-xs text-slate-500 mt-1">Cash/check at pickup. We'll mark this paid once received.</div>
            </div>
          ))}
        </Section>
      )}

      <Section title="✅ Paid history">
        {paid.length === 0 ? <div className="card-clay p-6 text-center text-slate-400">No paid orders yet.</div> : (
          <div className="card-clay overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="paid-table">
              <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-100"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Bags</th><th className="px-4 py-3">Tip</th><th className="px-4 py-3">Total</th></tr></thead>
              <tbody>
                {paid.map((o) => (
                  <tr key={o.id} className="border-b border-slate-50">
                    <td className="px-4 py-3">{(o.pickup_date || o.created_at).slice(0, 10)}</td>
                    <td className="px-4 py-3 font-semibold">{windowLabel(o.window)}{o.monthly_cleaning ? " + clean" : ""}</td>
                    <td className="px-4 py-3">{o.bags}</td>
                    <td className="px-4 py-3">${o.tip_amount.toFixed(2)}</td>
                    <td className="px-4 py-3 font-semibold">${o.total_amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mb-6">
    <h2 className="font-fredoka text-lg font-bold text-slate-900 mb-2">{title}</h2>
    <div className="space-y-2">{children}</div>
  </section>
);
